import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import RevolutCheckout from "@revolut/checkout";

import Topbar from "../../layout/Topbar";
import Footer from "../../layout/Footer";
import {
  TABS,
  TAB_NAME,
  SCREEN_PATH,
  EMPTY_STRING,
  subscriptionConstants,
} from "../../constants";
import {
  encryptObject,
  setItemToStorage,
  getDecryptPayload,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_PLATFORM,
} from "../../system";
import { hasAgentAccess } from "../../shared/utility";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import { subscriptionActions, authActions } from "../../redux/actions";
import SubscriptionCard from "./SubscriptionCard";

const { GET_SUBSCRIPTION_PLANS, SUBSCRIBE } = subscriptionConstants;

const SubscriptionPlan = (props) => {
  const {
    user,
    userRole,
    token,
    spinnerActions,
    subscriptionPlans,
    subscribe,
    resetToken,
    updateUser,
    getSubscriptionPlans,
  } = props;

  const canAllowWebSubscription =
    user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.WEB ||
    user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.NONE ||
    ((user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.ANDROID ||
      user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.IOS) &&
      user.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED);

  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState(TABS.MONTHLY);

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  const hasAgentRole = hasAgentAccess(userRole);

  useEffect(() => {
    getSubscriptionPlans(userRole);
    // eslint-disable-next-line
  }, [userRole]);

  useEffect(() => {
    if (subscriptionPlans) {
      setPlans(subscriptionPlans);
    }
  }, [subscriptionPlans]);

  const UpdateSubscriptionStatus = () => {
    const payload = getDecryptPayload();
    const ciphertext = encryptObject({
      ...payload,
      user: {
        ...payload.user,
        subscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
        subscriptionPlatform: SUBSCRIPTION_PLATFORM.WEB,
      },
    });
    setItemToStorage(process.env.REACT_APP_PAYLOAD, ciphertext);
    updateUser({
      ...payload.user,
      subscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
      subscriptionPlatform: SUBSCRIPTION_PLATFORM.WEB,
    });
    const path = hasAgentRole ? SCREEN_PATH.MY_LISTING : SCREEN_PATH.HOME;
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (token !== EMPTY_STRING) {
      RevolutCheckout(token, process.env.REACT_APP_REVOLUT_MODE).then(
        (instance) => {
          instance.payWithPopup({
            savePaymentMethodFor: process.env.REACT_APP_PAYMENT_METHOD_FOR,
            onSuccess() {
              UpdateSubscriptionStatus();
            },
          });
        }
      );
      resetToken();
    }
    // eslint-disable-next-line
  }, [token]);

  return (
    <>
      {(spinner(GET_SUBSCRIPTION_PLANS) > -1 || spinner(SUBSCRIBE) > -1) && (
        <MainSpinner className="backdrop-transparent" />
      )}
      <Topbar />
      <MainContentWrapper
        className={!canAllowWebSubscription ? "d-flex align-items-center" : ""}
      >
        <div className="subscription-plans container">
          {canAllowWebSubscription ? (
            <>
              <p className="description">
                {hasAgentRole ? (
                  <>
                    We offer great <span className="price">price</span> plans
                    for our agents
                  </>
                ) : (
                  <>Discover What Feels Like Home</>
                )}
              </p>
              {!hasAgentRole && (
                <div>
                  <p className="trial-description">
                    Explore everything Bayview Shores has to offer with a 30-day
                    free trial. To activate your trial, simply register your
                    card details—no charges will be made unless you choose to
                    continue after your trial ends. Cancel anytime within 30
                    days to avoid billing.
                  </p>
                </div>
              )}
              {hasAgentRole && (
                <div className="tabs-container">
                  {Object.values(TABS).map((tab) => (
                    <button
                      key={tab}
                      className={`tab ${
                        activeTab === tab ? "active" : EMPTY_STRING
                      }`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {TAB_NAME[tab]}
                    </button>
                  ))}
                </div>
              )}
              <div className="pricing-container">
                {plans
                  .filter(
                    (plan) => !hasAgentRole || plan.planType === activeTab
                  )
                  .map((plan) => (
                    <SubscriptionCard
                      key={plan.id}
                      plan={plan}
                      subscribe={subscribe}
                      isAgent={hasAgentRole}
                      isNotSubscribed={
                        user.subscriptionStatus ===
                        SUBSCRIPTION_STATUS.NOT_SUBSCRIBED
                      }
                    />
                  ))}

                {hasAgentRole && (
                  <p className="discounted-description">
                    *Biannual and Annual discounted offers available online.
                    Early Adopter program expires in Oct 2025.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="subscription-message">
              {`To manage or cancel your subscription, please go to the ${
                user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.ANDROID
                  ? "Google Play"
                  : "Apple App"
              } Store.`}
            </div>
          )}
        </div>
      </MainContentWrapper>
      <Footer />
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  userRole: state.authReducer.userRole,
  token: state.subscriptionReducer.token,
  spinnerActions: state.systemReducer.spinnerActions,
  subscriptionPlans: state.subscriptionReducer.subscriptionPlans,
});

const mapDispatchToProps = (dispatch) => ({
  getSubscriptionPlans: (roleId) =>
    dispatch(subscriptionActions.getSubscriptionPlans(roleId)),
  updateUser: (data) => dispatch(authActions.updateUser(data)),
  subscribe: (id) => dispatch(subscriptionActions.subscribe(id)),
  resetToken: () => dispatch(subscriptionActions.resetToken()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionPlan);
